import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Eye, Heart, Pause, Play, Volume2, VolumeX } from 'lucide-react';
import { SHOWCASE_BRANDS, SHOWCASE_VIDEOS, type ShowcaseVideo } from '../data/showcase';
import { platformIcon } from './PlatformIcons';
import { formatCompact } from '../lib/format';

/** How far each off-centre tile slides, as a % of its own width. */
const TILE_OFFSET = 62;
/** Tiles more than this many steps from centre are hidden entirely. */
const VISIBLE_RADIUS = 2;
/** Autoplay dwell per tile, in ms. Pauses while a video is playing. */
const ROTATE_MS = 4000;

/**
 * Coverflow-style carousel of creator videos.
 *
 * Only one video plays at a time and every other one is rewound, so the page
 * never has 14 decoders running. Off-screen tiles also skip preloading
 * entirely - with ~120MB of MP4 in this section, preloading all of them would
 * be the single heaviest thing on the site.
 */
export function Showcase() {
  const videos = SHOWCASE_VIDEOS;
  const total = videos.length;

  const totals = useMemo(() => {
    let views = 0;
    let likes = 0;
    for (const v of videos) {
      views += v.views ?? 0;
      likes += v.likes ?? 0;
    }
    return { views, likes };
  }, [videos]);

  const [active, setActive] = useState(0);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(false);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);

  const activeBrand = SHOWCASE_BRANDS[videos[active].brand];

  // Moving to a new tile always lands paused - autoplaying with sound as the
  // carousel rotates past would be hostile.
  useEffect(() => {
    setPlaying(false);
  }, [active]);

  useEffect(() => {
    videoRefs.current.forEach((vid, i) => {
      if (!vid) return;

      if (i === active) {
        vid.muted = muted;
        if (playing) {
          // Autoplay rejects when the tab is backgrounded or the gesture
          // wasn't trusted; there is nothing useful to do about it.
          void vid.play().catch(() => {});
        } else {
          vid.pause();
        }
        return;
      }

      vid.pause();
      try {
        vid.currentTime = 0;
      } catch {
        /* Seeking before metadata loads throws; the tile is offscreen anyway. */
      }
    });
  }, [active, muted, playing]);

  // Auto-advance, but never while the visitor is actually watching something.
  useEffect(() => {
    if (playing) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const id = window.setInterval(() => setActive((prev) => (prev + 1) % total), ROTATE_MS);
    return () => window.clearInterval(id);
  }, [playing, total]);

  const go = (delta: number) => setActive((prev) => (prev + delta + total) % total);

  return (
    <section id="work" className="bg-ink text-cream relative overflow-hidden">
      <div className="relative mx-auto max-w-6xl px-6 py-24 md:py-32">
        <div className="mx-auto max-w-2xl text-center">
          <p className="marketing-label text-cream/60">Recent work</p>
          <h2 className="font-display mt-4 text-4xl leading-tight md:text-5xl">
            Real creators.
            <span className="text-cream/60"> Real numbers.</span>
          </h2>
          <p className="text-cream/70 mt-5 text-base leading-relaxed">
            {total} creator posts from our influencer campaigns across TikTok and Reels &mdash;{' '}
            <span className="text-cream font-medium">{formatCompact(totals.views)} views</span>{' '}
            and{' '}
            <span className="text-cream font-medium">{formatCompact(totals.likes)} likes</span>{' '}
            and counting. Tap any tile to play.
          </p>
        </div>

        <div className="relative mt-14">
          <div
            className="relative mx-auto h-[500px] w-full overflow-hidden sm:h-[560px]"
            aria-roledescription="carousel"
            aria-label="Featured creator videos"
          >
            {videos.map((video, i) => (
              <Tile
                key={video.id}
                video={video}
                index={i}
                active={active}
                total={total}
                muted={muted}
                playing={playing}
                onSelect={() => setActive(i)}
                onTogglePlay={() => setPlaying((p) => !p)}
                onToggleMute={() => setMuted((m) => !m)}
                registerRef={(el) => {
                  videoRefs.current[i] = el;
                }}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={() => go(-1)}
            aria-label="Previous video"
            className="bg-cream/10 text-cream ring-cream/20 hover:bg-cream/20 focus-visible:ring-rust absolute top-1/2 left-2 z-20 flex size-10 -translate-y-1/2 items-center justify-center rounded-full ring-1 backdrop-blur transition-colors duration-200 focus:outline-none focus-visible:ring-2 sm:left-4 md:left-8"
          >
            <ChevronLeft className="size-5" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => go(1)}
            aria-label="Next video"
            className="bg-cream/10 text-cream ring-cream/20 hover:bg-cream/20 focus-visible:ring-rust absolute top-1/2 right-2 z-20 flex size-10 -translate-y-1/2 items-center justify-center rounded-full ring-1 backdrop-blur transition-colors duration-200 focus:outline-none focus-visible:ring-2 sm:right-4 md:right-8"
          >
            <ChevronRight className="size-5" aria-hidden />
          </button>
        </div>

        <div className="mt-8 flex flex-wrap justify-center gap-2">
          {videos.map((video, i) => (
            <button
              key={video.id}
              type="button"
              onClick={() => setActive(i)}
              aria-label={`Go to video ${i + 1} (${video.handle})`}
              aria-current={i === active}
              className={`h-1.5 rounded-full transition-[width,background-color] duration-200 ${
                i === active ? 'bg-cream w-6' : 'bg-cream/30 hover:bg-cream/60 w-1.5'
              }`}
            />
          ))}
        </div>

        <p className="text-cream/60 mt-8 text-center text-xs">
          For{' '}
          <a
            href={activeBrand.url}
            target="_blank"
            rel="noreferrer noopener"
            className="text-cream/80 hover:text-cream transition-colors duration-200"
          >
            {activeBrand.name}
          </a>
        </p>
      </div>
    </section>
  );
}

interface TileProps {
  video: ShowcaseVideo;
  index: number;
  active: number;
  total: number;
  muted: boolean;
  playing: boolean;
  onSelect: () => void;
  onTogglePlay: () => void;
  onToggleMute: () => void;
  registerRef: (el: HTMLVideoElement | null) => void;
}

function Tile({
  video,
  index,
  active,
  total,
  muted,
  playing,
  onSelect,
  onTogglePlay,
  onToggleMute,
  registerRef,
}: TileProps) {
  // Wrap the offset into [-total/2, total/2] so tiles travel the short way
  // around the loop instead of sliding all the way back across the stage.
  const half = total / 2;
  let offset = index - active;
  if (offset > half) offset -= total;
  if (offset < -half) offset += total;

  const abs = Math.abs(offset);
  const isActive = offset === 0;
  const isNeighbor = abs === 1;
  const PlatformIcon = platformIcon(video.platform);

  const label = isActive
    ? `Video by ${video.handle}${
        video.views != null ? `, ${formatCompact(video.views)} views` : ''
      }. ${playing ? 'Tap to pause.' : 'Tap to play.'}`
    : `Show video by ${video.handle}`;

  return (
    <div
      role="button"
      tabIndex={isActive ? 0 : -1}
      // `inert` keeps the off-centre tiles out of the a11y tree and tab order
      // even though they are still painted. React 19 types this as a boolean.
      inert={!isActive}
      aria-label={label}
      onClick={() => (isActive ? onTogglePlay() : onSelect())}
      onKeyDown={(e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        if (isActive) onTogglePlay();
        else onSelect();
      }}
      style={{
        transform: `translate(-50%, -50%) translateX(${offset * TILE_OFFSET}%) scale(${
          isActive ? 1 : 0.78
        })`,
        opacity: abs > VISIBLE_RADIUS ? 0 : isActive ? 1 : 0.4,
        zIndex: 10 - abs,
        filter: isActive ? 'none' : 'saturate(0.85)',
      }}
      className="group ring-cream/10 hover:ring-cream/30 focus-visible:ring-cream absolute top-1/2 left-1/2 block aspect-[9/16] h-[440px] cursor-pointer overflow-hidden rounded-xl bg-neutral-900 shadow-2xl shadow-black/50 ring-1 transition-[transform,opacity,filter] duration-500 ease-out select-none focus:outline-none focus-visible:ring-2 sm:h-[500px]"
    >
      <video
        ref={registerRef}
        src={video.src}
        poster={video.poster}
        playsInline
        loop
        muted
        // Only the visible three tiles touch the network. `metadata` (not
        // `auto`) means we fetch headers, not 10MB of video, per tile.
        preload={isActive || isNeighbor ? 'metadata' : 'none'}
        draggable={false}
        className="absolute inset-0 h-full w-full bg-black object-cover select-none"
      />

      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/60 to-transparent"
      />
      <div
        aria-hidden
        className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/80 to-transparent"
      />

      <div className="absolute inset-x-3 top-3 flex items-center justify-between gap-2">
        <span className="inline-flex max-w-[75%] items-center gap-1.5 truncate rounded-full bg-black/55 px-2.5 py-1 text-xs font-medium text-white backdrop-blur">
          <PlatformIcon className="size-3.5 shrink-0" />
          <span className="truncate">{video.handle}</span>
        </span>

        {isActive ? (
          <button
            type="button"
            onClick={(e) => {
              // Without this the click also hits the tile and toggles playback.
              e.stopPropagation();
              onToggleMute();
            }}
            aria-label={muted ? 'Unmute' : 'Mute'}
            className="focus-visible:ring-rust inline-flex size-8 items-center justify-center rounded-full bg-black/55 text-white ring-1 ring-white/20 backdrop-blur transition-colors duration-200 hover:bg-black/75 focus:outline-none focus-visible:ring-2"
          >
            {muted ? (
              <VolumeX className="size-4" aria-hidden />
            ) : (
              <Volume2 className="size-4" aria-hidden />
            )}
          </button>
        ) : null}
      </div>

      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <span
          className={`flex size-16 items-center justify-center rounded-full bg-white/15 ring-1 ring-white/30 backdrop-blur transition-opacity duration-200 ${
            isActive && !playing ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
          }`}
        >
          {playing ? (
            <Pause className="size-7 fill-white text-white" aria-hidden />
          ) : (
            <Play className="ml-0.5 size-7 fill-white text-white" aria-hidden />
          )}
        </span>
      </div>

      <div className="absolute inset-x-3 bottom-3 flex items-center justify-between gap-2 text-xs font-medium">
        {video.views != null ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-black/55 px-2.5 py-1 text-white backdrop-blur">
            <Eye className="size-3.5" aria-hidden />
            {formatCompact(video.views)}
          </span>
        ) : (
          <span />
        )}
        {video.likes != null ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-black/55 px-2.5 py-1 text-white backdrop-blur">
            <Heart className="size-3.5 fill-rose-400 text-rose-400" aria-hidden />
            {formatCompact(video.likes)}
          </span>
        ) : null}
      </div>
    </div>
  );
}
